import { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { getUserFromCookie } from '../helpers/api/apiCore';
import { initSocket, getSocket } from './index';
import { all as fetchNotifications } from '../helpers/api/notifications';
import notifySound from '../assets/sound/mixkit-bell-notification-933.wav';

// Bridge component: mounts once at app level and wires socket events to state
export default function NotificationsSocketBridge() {
	const queryClient = useQueryClient();
	const mounted = useRef(false);
	const audioRef = useRef<HTMLAudioElement | null>(null);

	useEffect(() => {
		if (mounted.current) return; // ensure single init
		mounted.current = true;

		const user = getUserFromCookie();
		const s = initSocket(user?.access_token || undefined);
		if (!s) return;

		// Prepare audio element and set up a one-time user-gesture unlock to satisfy autoplay policies
		if (!audioRef.current) {
			audioRef.current = new Audio(notifySound);
			try {
				audioRef.current.preload = 'auto';
			} catch {}
		}
		const tryUnlock = () => {
			const el = audioRef.current;
			if (!el) return;
			el.muted = true;
			void el.play()?.then(() => {
				el.pause();
				el.currentTime = 0;
				el.muted = false;
				window.removeEventListener('pointerdown', tryUnlock);
				window.removeEventListener('touchstart', tryUnlock);
				window.removeEventListener('click', tryUnlock);
			}).catch(() => {
				// keep listeners until a successful gesture occurs
			});
		};
		window.addEventListener('pointerdown', tryUnlock, { once: true });
		window.addEventListener('touchstart', tryUnlock, { once: true });
		window.addEventListener('click', tryUnlock, { once: true });

		const onWelcome = (_payload: any) => {
			// optional: console.debug('socket welcome', _payload);
		};

			const onNewNotification = (notification: any) => {
			// Update notifications cache: assume cache shape is an array
			queryClient.setQueryData<any[]>(['notifications'], (prev) => {
				const list = Array.isArray(prev) ? prev : [];
				return [notification, ...list];
			});

				// Play sound (throttled by reusing the same element)
				try {
					if (!audioRef.current) {
						audioRef.current = new Audio(notifySound);
					}
					audioRef.current.currentTime = 0;
					// Play and catch to prevent unhandled NotAllowedError when no user gesture yet
					audioRef.current.play()?.catch(() => { /* audio blocked by autoplay policy, toast will still show */ });
				} catch {}

				// Show toast (top-right), with title + description
				const title = notification?.name || 'New notification';
				const description = notification?.description || '';
				toast(
					<div>
						<strong>{title}</strong>
						{description ? <div style={{ fontSize: 12 }}>{description}</div> : null}
					</div>,
					{ position: 'top-right', autoClose: 5000 }
				);
		};

			const onConnect = () => {
				// Mark online and re-sync from REST to cover any missed events
				queryClient.setQueryData(['socket', 'connected'], true);
				queryClient.invalidateQueries(['notifications']);
			};

			const onDisconnect = () => {
				// Mark offline for UI indicator
				queryClient.setQueryData(['socket', 'connected'], false);
			};

		s.on('welcome', onWelcome);
		s.on('notification:new', onNewNotification);
		s.on('connect', onConnect);
		s.on('disconnect', onDisconnect);

		// Seed connection status and ensure initial notifications list exists
		queryClient.setQueryData(['socket', 'connected'], s.connected);
		// Ensure initial load exists in cache; if not, fetch once
		const existing = queryClient.getQueryData(['notifications']);
		if (!existing) {
			queryClient.prefetchQuery(['notifications'], async () => {
				const res = await fetchNotifications();
				return res?.data?.data ?? [];
			});
		}

		return () => {
			const sock = getSocket();
			sock?.off('welcome', onWelcome);
			sock?.off('notification:new', onNewNotification);
			sock?.off('connect', onConnect);
			sock?.off('disconnect', onDisconnect);

			window.removeEventListener('pointerdown', tryUnlock);
			window.removeEventListener('touchstart', tryUnlock);
			window.removeEventListener('click', tryUnlock);
		};
	}, [queryClient]);

	return null;
}
